/**
 * ConfigTab Component
 * Handles the configuration tab for workout routine settings
 * Requirements: 1.1, 1.2
 */

import * as React from 'react';
import {memo, useCallback, useEffect} from 'react';
import {Controller} from 'react-hook-form';
import { Input as TextInput } from '../../../../../../components/ui';
import { RadioGroup } from '../../../../../../components/ui';
import styled from 'styled-components';

import {Card, Link} from '../../../../../elements';
import Radio from '../../../../../elements/Radio';
import {DatePicker} from '../../../../../components/DatePicker';
import {UserSelect} from '../../../../../components/users';
import {ProgramSelect} from '../../../../../components/programas';

import type {ConfigTabProps} from './ConfigTab.type';
import type {SelectRangeProps, TargetProps} from '../../../../../types';

// Styled components
const WorkoutRoutineTarget = styled.div<{active: boolean}>`
  opacity: ${(props) => (props.active ? 1 : 0.5)};
  transition: opacity 0.2s ease;
`;

const WeekDaysGroup = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  margin-top: 10px;
`;

const WeekDaysItem = styled.div`
  display: inline-flex;
  align-items: center;
`;

/**
 * Format date to YYYY-MM-DD string
 */
const formatDate = (date: Date | string): string => {
  if (typeof date === 'string') {
    return date;
  }
  return date.toISOString().split('T')[0];
};

/**
 * ConfigTab Component
 * Manages the configuration settings for a workout routine
 */
export const ConfigTab = function ConfigTab({
  form,
  daysWeek,
  users,
  programs,
  isLoading,
  onSave,
}: ConfigTabProps): JSX.Element {
  const {control, watch, setValue, formState} = form;
  const {errors} = formState;

  // Watch form values
  const target = watch('target');
  const cycleType = watch('cycleType');
  const startDate = watch('startDate');
  const endDate = watch('endDate');
  const showDaysOfWeek = watch('showDaysOfWeek') || false;
  const weekDayIds = watch('weekDayIds') || [];
  const programsSelected = watch('programsSelected') || [];
  const usersTarget = watch('usersTarget') || [];

  // Handle date range selection
  const handleDateSelect = useCallback(
    ({startDate: newStartDate, endDate: newEndDate}: SelectRangeProps) => {
      setValue('startDate', formatDate(newStartDate));
      setValue('endDate', formatDate(newEndDate));
    },
    [setValue],
  );

  // Get date range for DatePicker
  const getDateRange = useCallback((): SelectRangeProps => {
    const dateRange = {startDate: new Date(), endDate: new Date()};
    if (startDate) {
      if (typeof startDate === 'string') {
        const [year, month, day] = startDate.split('-').map(Number);
        dateRange.startDate = new Date(year, month - 1, day);
      } else if (typeof startDate === 'object') {
        dateRange.startDate = startDate as Date;
      }
    }
    if (endDate) {
      if (typeof endDate === 'string') {
        const [year, month, day] = endDate.split('-').map(Number);
        dateRange.endDate = new Date(year, month - 1, day);
      } else if (typeof endDate === 'object') {
        dateRange.endDate = endDate as Date;
      }
    }
    return dateRange;
  }, [startDate, endDate]);

  // Handle programs selection
  const handleProgramsSelect = useCallback(
    (values: Array<{value: string | number; label: string}> | null) => {
      const programIds = (values || []).map((val) => val.value);
      setValue('programIds', programIds);
      setValue('programsSelected', values || []);
    },
    [setValue],
  );

  // Handle users selection
  const handleUsersSelect = useCallback(
    (values: Array<{value: string | number; label: string}> | null) => {
      const userIds = (values || []).map((val) => val.value);
      setValue('usersTargetIds', userIds);
      setValue('usersTarget', values || []);
    },
    [setValue],
  );

  // Handle week day toggle
  const handleDayChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const value = event.target.value;
      const currentDays = [...(weekDayIds || [])];
      const index = currentDays.findIndex((d) => String(d) === String(value));

      if (index > -1) {
        currentDays.splice(index, 1);
      } else {
        currentDays.push(value);
      }

      setValue('weekDayIds', currentDays);
    },
    [weekDayIds, setValue],
  );

  // Handle form submission
  const handleSubmit = useCallback(
    (event: React.FormEvent) => {
      console.log('submit');
      event.preventDefault();
      onSave();
    },
    [onSave],
  );

  return (
    <Card>
      <form className="form-workout-group-routine" onSubmit={handleSubmit}>
        <div className="row">
          <Controller
            name="name"
            control={control}
            render={({field, fieldState}) => (
              <TextInput
                s={12}
                id="name"
                name={field.name}
                onChange={field.onChange}
                onBlur={field.onBlur}
                value={field.value || ''}
                inputClassName={fieldState.error ? 'invalid' : ''}
                error={fieldState.error?.message}
                label="Nome da rotina de treino"
              />
            )}
          />
        </div>

        <div className="row">
          <div className="col s12">
            <div id="cycle_types" className="section">
              <h5>Qual tipo de treino?</h5>
              <Controller
                name="cycleType"
                control={control}
                render={({field, fieldState}) => (
                  <RadioGroup
                    label="Período (data)"
                    name={field.name}
                    onChange={field.onChange}
                    options={[{label: 'Período (data)', value: 'date'}]}
                    value={field.value || 'date'}
                    withGap
                  />
                )}
              />
              <div className="subtitle">
                Escolha um período de datas de quando o treino estará disponível
              </div>
              <DatePicker
                onSelect={handleDateSelect}
                selectRange={getDateRange()}
              />
            </div>
          </div>

          <div className="col s12">
            {!showDaysOfWeek && (
              <Link
                className="item-title"
                title="Restringir por dia da semana"
                onPress={() => setValue('showDaysOfWeek', true)}
              />
            )}
            {showDaysOfWeek && (
              <>
                <Link
                  className="item-title"
                  title="Não restringir por dia da semana"
                  onPress={() => {
                    setValue('weekDayIds', []);
                    setValue('showDaysOfWeek', false);
                  }}
                />
                <WeekDaysGroup className="input-field">
                  {daysWeek.map((day) => (
                    <WeekDaysItem key={day.id} className="week-days-group">
                      <label>
                        <input
                          type="checkbox"
                          name="weekDayIds"
                          value={String(day.id)}
                          onChange={handleDayChange}
                          checked={weekDayIds.some(
                            (id) => String(id) === String(day.id),
                          )}
                        />
                        <span>{day.short}</span>
                      </label>
                    </WeekDaysItem>
                  ))}
                </WeekDaysGroup>
              </>
            )}
          </div>
        </div>

        <div className="row">
          <div className="col s12">
            <h5>Para quem é o treino?</h5>
            <div id="targets" className="section">
              <WorkoutRoutineTarget
                className="row"
                active={target === 'programs'}>
                <div className="col s12">
                  <Controller
                    name="target"
                    control={control}
                    render={({field}) => (
                      <Radio
                        id="target-programs"
                        value="programs"
                        label="Para alunos ativos nos programas de treino abaixo"
                        checked={field.value === 'programs'}
                        name="workout_group_routine"
                        onChange={field.onChange}
                      />
                    )}
                  />
                  <div className="subtitle">
                    Deixe esse treino disponível para programas de treinos
                    (treinos em grupo)
                  </div>
                </div>
                <div className="input-field col s12 input-select">
                  <ProgramSelect
                    defaultOptions={programs}
                    onSelect={handleProgramsSelect}
                    selectValue={programsSelected}
                  />
                </div>
              </WorkoutRoutineTarget>

              <WorkoutRoutineTarget className="row" active={target === 'users'}>
                <div className="col s12">
                  <Controller
                    name="target"
                    control={control}
                    render={({field}) => (
                      <Radio
                        id="target-users"
                        value="users"
                        label="Para uma pessoa ou grupo"
                        checked={field.value === 'users'}
                        name="workout_group_routine"
                        onChange={field.onChange}
                      />
                    )}
                  />
                  <div className="subtitle">
                    Esse treino é para um ou mais atletas que você especificar
                  </div>
                </div>
                <div className="input-field col s12 input-select">
                  <UserSelect
                    defaultOptions={users}
                    onSelect={handleUsersSelect}
                    selectValue={usersTarget}
                  />
                </div>
              </WorkoutRoutineTarget>
            </div>
          </div>
        </div>

        <div className="row">
          <div className="col s12">
            <button
              className="btn btn-primary"
              type="submit"
              onClick={handleSubmit}
              disabled={isLoading}>
              {isLoading ? 'Salvando...' : 'Salvar & continuar'}
            </button>
          </div>
        </div>
      </form>
    </Card>
  );
};

export default ConfigTab;
